import { useCallback, useEffect, useId, useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

import { DataTable, Disclose, EmptyState, Loading, Refusal } from "@lopie/kernel-ui";

import { capLine, rowsOf, sharedLine } from "./openSandboxes.js";

const HELD = { overflowWrap: "anywhere", minWidth: 0 };

/**
 * Every open sandbox in the org, its age and estimated cost, and a cancel for a
 * governor (LO-198); and the approver's own switch for the emails about approvals
 * they narrowed (LO-200).
 *
 * A sandbox is torn down on a decision and never on a timer, so this list is where
 * a forgotten one gets decided. Drafts are here though the queue below leaves them
 * out: a draft is its author's to submit, and a governor still has to be able to
 * close one that has been left open.
 */
export default function OpenSandboxes({ get, post, onChanged }) {
  const [body, setBody] = useState(null);
  const [failure, setFailure] = useState(null);
  const [confirming, setConfirming] = useState(null); // a row
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // { app, body } | { app, failure }
  const [prefs, setPrefs] = useState(null);
  const [prefsFailure, setPrefsFailure] = useState(null);
  const reasonId = useId();

  const load = useCallback(async () => {
    try {
      setBody(await get("/sandboxes"));
      setFailure(null);
    } catch (e) {
      setFailure(e);
    }
  }, [get]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    let live = true;
    get("/notices")
      .then((b) => { if (live) setPrefs(b); })
      .catch((e) => { if (live) setPrefsFailure(e); });
    return () => { live = false; };
  }, [get]);

  const cancel = async (row) => {
    setBusy(true);
    try {
      const answer = await post(row.cancelPath, reason.trim() ? { reason: reason.trim() } : {});
      setDone({ app: row.app, body: answer });
      setConfirming(null);
      setReason("");
      await load();
      onChanged?.();
    } catch (e) {
      setDone({ app: row.app, failure: e });
    } finally {
      setBusy(false);
    }
  };

  const flip = async (on) => {
    try {
      setPrefs(await post("/notices", { kind: "approvals", on }));
      setPrefsFailure(null);
    } catch (e) {
      setPrefsFailure(e);
    }
  };

  const rows = rowsOf(body);
  const cap = capLine(body);
  const approvals = (prefs?.kinds || []).find((k) => k.kind === "approvals");

  const columns = [
    { key: "app", label: "App", render: (r) => <span style={HELD}>{r.app}</span> },
    { key: "state", label: "State" },
    { key: "author", label: "Author", render: (r) => <span style={HELD}>{r.author}</span> },
    { key: "age", label: "Open for", align: "right" },
    { key: "cost", label: "Cost · estimate", align: "right",
      render: (r) => <span title={r.costTitle}>{r.cost}</span> },
    { key: "quiet", label: "Last activity" },
    { key: "act", label: "", render: (r) => (r.canCancel ? (
      <Button size="small" color="warning" onClick={() => { setConfirming(r); setDone(null); }}
        disabled={busy}>
        Cancel…
      </Button>
    ) : (
      <Typography variant="caption" color="text.secondary">Its author can cancel it</Typography>
    )) },
  ];

  return (
    <Stack spacing={1.25} component="section" aria-label="Open sandboxes">
      <Stack direction="row" spacing={1} sx={{ alignItems: "baseline", flexWrap: "wrap" }}>
        <Typography variant="h6" component="h2">Open sandboxes</Typography>
        {cap.text ? (
          <Typography variant="body2" color={cap.full ? "warning.main" : "text.secondary"}>
            {cap.text}{cap.full ? ": a new draft that names a build is refused until one closes" : ""}
          </Typography>
        ) : null}
      </Stack>

      {failure ? <Refusal failure={failure} actor="This console" onRetry={load} /> : null}
      {!body && !failure ? <Loading label="Reading the open sandboxes" minHeight={60} size={20} /> : null}

      {body ? (
        <DataTable columns={columns} rows={rows}
          empty={<EmptyState title="No open sandboxes">No draft or pending request names a build right now.</EmptyState>} />
      ) : null}

      {confirming ? (
        <Alert severity="warning" variant="outlined">
          <Stack spacing={1}>
            <Typography variant="body2" sx={HELD}>
              Cancel {confirming.author}&rsquo;s {confirming.state.toLowerCase()} of {confirming.app}? The request is
              closed and its sandbox is torn down within a few minutes. Whatever version of the app is already
              approved stays in force.
            </Typography>
            <TextField size="small" label="Reason (optional, the author sees it)" value={reason}
              onChange={(e) => setReason(e.target.value)} id={reasonId} />
            <Stack direction="row" spacing={1}>
              <Button size="small" color="warning" variant="contained" disabled={busy}
                onClick={() => cancel(confirming)}>
                {busy ? "Cancelling…" : "Cancel the request"}
              </Button>
              <Button size="small" disabled={busy} onClick={() => setConfirming(null)}>Keep it</Button>
            </Stack>
          </Stack>
        </Alert>
      ) : null}

      {done?.body ? (
        <Alert severity="success" variant="outlined" onClose={() => setDone(null)}>
          {done.app}: cancelled. {(done.body.does_not_prove || []).join(" ")}
        </Alert>
      ) : null}
      {done?.failure ? <Refusal failure={done.failure} actor="This console" /> : null}

      {body ? (
        <Disclose label="How the cost is estimated" align="row"
          summary="Each figure is list prices applied to what a sandbox is built from, not a bill.">
          <Stack spacing={0.5}>
            {sharedLine(body) ? (
              <Typography variant="body2" color="text.secondary" sx={HELD}>{sharedLine(body)}</Typography>
            ) : null}
            {(body.does_not_prove || []).map((t, i) => (
              <Typography key={i} variant="body2" color="text.secondary" sx={HELD}>{t}</Typography>
            ))}
            <Typography variant="body2" color="text.secondary" sx={HELD}>
              An idle draft&rsquo;s author is emailed after {body.idle_chase_after_days} days with no new build, QA run
              or read, and once more at twice that. Nothing is removed on a timer.
            </Typography>
          </Stack>
        </Disclose>
      ) : null}

      {approvals ? (
        <Box>
          <FormControlLabel
            control={<Switch size="small" checked={Boolean(approvals.on)} onChange={(e) => flip(e.target.checked)} />}
            label={<Typography variant="body2">Email me about approvals I narrowed: {approvals.on ? "on" : "off"}</Typography>}
          />
          <Typography variant="caption" color="text.secondary" component="p" sx={HELD}>
            {approvals.detail}. This changes only your own emails.
          </Typography>
        </Box>
      ) : null}
      {prefsFailure ? (
        <Typography variant="caption" color="text.secondary" sx={HELD}>
          Your email setting could not be read: {prefsFailure.message || "no answer"}.
        </Typography>
      ) : null}
    </Stack>
  );
}
